import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, RefreshCw, CreditCard, ArrowDownToLine, History, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const PACKAGES = [
  { key: "topup_5", pay: 5, credit: 5 },
  { key: "topup_10", pay: 10, credit: 10 },
  { key: "topup_25", pay: 25, credit: 28 },
  { key: "topup_50", pay: 50, credit: 60 },
  { key: "topup_100", pay: 100, credit: 130 },
];

const TX_COLOR = {
  buy_in: "text-loss", withdraw_request: "text-loss",
  payout: "text-profit", refund: "text-profit", demo_reload: "text-signal", deposit: "text-profit",
};

function fmt2(n) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const Cashier = () => {
  const { t } = useLang();
  const { user, refreshUser } = useAuth();
  const [txns, setTxns] = useState([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);

  const withdrawable = Number(user?.withdrawable ?? 0);

  const loadTxns = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet/transactions");
      setTxns(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadTxns(); }, [loadTxns]);

  const reloadDemo = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/reload-demo");
      await refreshUser();
      await loadTxns();
      toast.success(t("reloaded_demo"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deposit = async (pkg) => {
    setLoadingKey(pkg.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: pkg.key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setLoadingKey(null);
    }
  };

  const withdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return;
    setBusy(true);
    try {
      await api.post("/wallet/withdraw-request", { amount: amt });
      setAmount("");
      await refreshUser();
      await loadTxns();
      toast.success(t("withdraw_pending"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const txLabel = (type) => t(`tx_${type}`) || type;

  const chartData = [...txns]
    .filter((tx) => typeof tx.balance_after === "number")
    .reverse()
    .map((tx, i) => ({ i: i + 1, balance: tx.balance_after }));

  return (
    <div data-testid="cashier" className="space-y-8">
      <h2 className="font-heading font-bold text-xl flex items-center gap-2">
        <Wallet className="w-5 h-5 text-signal" /> {t("cashier")}
      </h2>

      {/* balance + demo reload */}
      <div className="border border-white/10 bg-panel p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex gap-10">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-white/40">{t("balance_label")}</div>
            <div data-testid="cashier-balance" className="font-mono font-bold text-3xl text-signal tabular-nums">
              €{fmt2(user?.wallet ?? 0)}
            </div>
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-white/40">{t("withdrawable")}</div>
            <div data-testid="cashier-withdrawable" className="font-mono font-bold text-3xl text-profit tabular-nums">
              €{fmt2(withdrawable)}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1">
          <Button
            data-testid="reload-demo-btn"
            onClick={reloadDemo}
            disabled={busy}
            variant="outline"
            className="border-signal/40 text-signal hover:bg-signal/10 gap-2 rounded-sm"
          >
            <RefreshCw className="w-4 h-4" /> {t("reload_demo_btn")}
          </Button>
          <span className="font-mono text-[11px] text-white/40 max-w-[220px] text-left sm:text-right">{t("reload_demo_desc")}</span>
        </div>
      </div>

      {/* balance trend chart */}
      {chartData.length > 1 && (
        <div className="border border-white/10 bg-panel p-6" data-testid="balance-chart">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-signal" />
            <span className="font-heading font-semibold">{t("balance_trend")}</span>
          </div>
          <div style={{ width: "100%", height: 140 }}>
            <ResponsiveContainer>
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f5c518" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f5c518" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="i" hide />
                <YAxis tick={{ fill: "#5b616b", fontSize: 10 }} width={40} />
                <Tooltip
                  contentStyle={{ background: "#14171c", border: "1px solid #23272e", fontFamily: "monospace", fontSize: 12 }}
                  labelStyle={{ color: "#8a8f98" }}
                  formatter={(v) => [`€${fmt2(v)}`, t("balance_label")]}
                />
                <Area type="monotone" dataKey="balance" stroke="#f5c518" strokeWidth={2} fill="url(#balGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* deposit packages */}
      <div className="border border-white/10 bg-panel p-6">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="w-4 h-4 text-white/60" />
          <span className="font-heading font-semibold">{t("deposit_title")}</span>
        </div>
        <p className="font-mono text-[11px] text-white/40 mb-4">{t("deposit_desc")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {PACKAGES.map((p) => {
            const bonus = p.credit - p.pay;
            return (
              <button
                key={p.key}
                data-testid={`deposit-${p.key}`}
                onClick={() => deposit(p)}
                disabled={loadingKey !== null}
                className="relative border border-white/10 hover:border-signal/60 bg-panelhover hover:bg-signal/5 transition-colors py-5 rounded-sm text-white disabled:opacity-40 flex flex-col items-center"
              >
                {bonus > 0 && (
                  <span className="absolute -top-2 right-2 bg-profit/20 text-profit font-mono text-[10px] font-bold px-1.5 py-0.5 rounded">
                    +€{bonus} {t("bonus_label")}
                  </span>
                )}
                <span className="font-mono font-bold text-lg">
                  {loadingKey === p.key ? t("processing") : `€${p.pay}`}
                </span>
                {bonus > 0 && loadingKey !== p.key && (
                  <span className="font-mono text-[10px] text-white/40 mt-0.5">
                    {t("you_get")} €{p.credit}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* withdraw request */}
      <div className="border border-white/10 bg-panel p-6">
        <div className="flex items-center gap-2 mb-1">
          <ArrowDownToLine className="w-4 h-4 text-white/60" />
          <span className="font-heading font-semibold">{t("withdraw_title")}</span>
        </div>
        <p className="font-mono text-[11px] text-white/40 mb-1">{t("withdraw_desc")}</p>
        <p className="font-mono text-[11px] text-white/40 mb-4" data-testid="withdrawable-note">
          {t("withdrawable_note")} · {t("withdrawable")}: <span className="text-profit">€{fmt2(withdrawable)}</span>
        </p>
        <div className="flex gap-3 max-w-sm">
          <Input
            data-testid="withdraw-amount"
            type="number"
            min="1"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="€"
            className="bg-base border-white/10 text-white font-mono"
          />
          <Button
            data-testid="withdraw-btn"
            onClick={withdraw}
            disabled={busy || !amount || withdrawable <= 0}
            className="bg-white/10 hover:bg-white/20 text-white rounded-sm whitespace-nowrap disabled:opacity-40"
          >
            {t("withdraw_btn")}
          </Button>
        </div>
      </div>

      {/* cash history */}
      <div>
        <h3 className="font-heading font-semibold text-lg mb-3 flex items-center gap-2">
          <History className="w-4 h-4 text-signal" /> {t("cash_history")}
        </h3>
        <div className="border border-white/10 bg-panel divide-y divide-white/5" data-testid="cash-history">
          {txns.length === 0 ? (
            <div className="p-5 font-mono text-sm text-white/40">{t("no_transactions")}</div>
          ) : (
            txns.map((tx) => (
              <div key={tx.id} data-testid={`txn-${tx.id}`} className="p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-white truncate">{txLabel(tx.type)}</div>
                  <div className="font-mono text-[11px] text-white/40">
                    {tx.ts ? new Date(tx.ts).toLocaleString() : ""}
                    {tx.commission > 0 && <> · {t("of_which_commission")} €{fmt2(tx.commission)}</>}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-mono font-bold tabular-nums ${TX_COLOR[tx.type] || "text-white"}`}>
                    {tx.amount >= 0 ? "+" : ""}{fmt2(tx.amount)}
                  </div>
                  {typeof tx.balance_after === "number" && (
                    <div className="font-mono text-[11px] text-white/30 tabular-nums">€{fmt2(tx.balance_after)}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
